/**
 * Hook for fetching feature flags for an org using React Query.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';
import {
  getPersistedFeatureFlags,
  setPersistedFeatureFlags,
} from '@/lib/localStorage/persistedFeatureFlags';

interface FeatureFlagsResponse {
  feature_flags: string[];
}

interface UseFeatureFlagsOptions {
  /**
   * When false, the network query is skipped entirely. Use this when the
   * caller has already determined the answer locally (e.g. a static flag)
   * and does not want to trigger an HTTP round-trip.
   */
  enabled?: boolean;
}

/**
 * Hook to fetch feature flags for an organization.
 *
 * Uses React Query for caching, deduplication, and automatic refetching.
 * Flags are cached for 5 minutes per org.
 *
 * @param orgId - Organization ID
 * @param options - Optional settings (e.g. `enabled: false` to skip the fetch)
 * @returns React Query result with feature flags array
 *
 * @example
 * const { data: flags, isLoading } = useFeatureFlags(orgId);
 */
export function useFeatureFlags(
  orgId: number | undefined,
  options: UseFeatureFlagsOptions = {}
) {
  const { enabled = true } = options;
  const result = useQuery({
    queryKey: ['featureFlags', orgId],
    queryFn: async (): Promise<string[]> => {
      if (!orgId) {
        return [];
      }
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/feature-flags/`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch feature flags: ${response.status}`);
      }
      const data: FeatureFlagsResponse = await response.json();
      return data.feature_flags || [];
    },
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000,
    initialData: () => getPersistedFeatureFlags(orgId),
    initialDataUpdatedAt: () => 0,
  });

  useEffect(() => {
    if (!orgId) {
      return;
    }
    if (!result.data) {
      return;
    }
    setPersistedFeatureFlags(orgId, result.data);
  }, [result.data, orgId]);

  return result;
}
